import React, { useEffect, useState, useCallback } from 'react';
import { laboratoryService, patientService } from '../services/api';
import './LaboratoriesDashboard.css';

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ESTADOS = [
  { value: 'pendiente',   label: 'Pendiente',   className: 'estado-pendiente' },
  { value: 'en_proceso',  label: 'En proceso',  className: 'estado-en_proceso' },
  { value: 'completado',  label: 'Completado',  className: 'estado-completado' },
  { value: 'entregado',   label: 'Entregado',   className: 'estado-entregado' },
  { value: 'cancelado',   label: 'Cancelado',   className: 'estado-cancelado' },
];

const getEstadoLabel = (value) => ESTADOS.find((e) => e.value === value)?.label || value;
const getEstadoClass = (value) => ESTADOS.find((e) => e.value === value)?.className || '';

const DocIcon = ({ tipo }) => {
  if (tipo?.includes('pdf')) return <span className="doc-type-icon pdf">PDF</span>;
  if (tipo?.includes('image')) return <span className="doc-type-icon img">IMG</span>;
  if (tipo?.includes('word') || tipo?.includes('document')) return <span className="doc-type-icon doc">DOC</span>;
  return <span className="doc-type-icon">FILE</span>;
};

// ─── Panel de historial de paciente ──────────────────────────────────────────
const PatientHistoryPanel = ({ pacienteId, pacienteNombre, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await laboratoryService.getPatientHistory(pacienteId);
        setHistorial(res.data);
      } catch {
        setError('No se pudo cargar el historial del paciente');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pacienteId]);

  const handleOpenDocument = async (doc, download = false) => {
    try {
      await laboratoryService.openDocument(doc.id, doc.nombre_original, download);
    } catch {
      setError('No se pudo abrir el documento');
    }
  };

  return (
    <div className="historial-overlay" onClick={onClose}>
      <div className="historial-panel" onClick={(e) => e.stopPropagation()}>
        <div className="historial-header">
          <div>
            <h3>Historial de laboratorios</h3>
            <p className="historial-nombre">{pacienteNombre}</p>
          </div>
          <button className="historial-close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {loading && <p className="historial-loading">Cargando historial...</p>}
        {error && <div className="laboratories-message error">{error}</div>}

        {historial && !loading && (
          <>
            <p className="historial-summary">
              {historial.solicitudes.length} solicitud(es) · {historial.documentos.length} documento(s)
            </p>

            {historial.solicitudes.length === 0 ? (
              <p className="historial-empty">Sin solicitudes de laboratorio registradas.</p>
            ) : (
              <div className="historial-list">
                {historial.solicitudes.map((sol) => {
                  const docs = historial.documentos.filter((d) => d.solicitud_id === sol.id);
                  return (
                    <div key={sol.id} className="historial-item">
                      <div className="historial-item-header">
                        <span className={`estado-badge ${getEstadoClass(sol.estado)}`}>
                          {getEstadoLabel(sol.estado)}
                        </span>
                        <span className="historial-fecha">{formatDate(sol.created_at)}</span>
                      </div>
                      <p className="historial-detalle">{sol.detalle_solicitud}</p>
                      <p className="historial-meta">Solicitó: {sol.solicitado_por_nombre}</p>

                      {docs.length > 0 && (
                        <div className="historial-docs">
                          {docs.map((doc) => (
                            <div key={doc.id} className="historial-doc-row">
                              <button
                                type="button"
                                className="historial-doc-link"
                                onClick={() => handleOpenDocument(doc)}
                              >
                                <DocIcon tipo={doc.tipo_mime} />
                                <span>{doc.nombre_original}</span>
                                <small>{formatSize(doc.tamano)}</small>
                              </button>
                              <button
                                type="button"
                                className="historial-doc-download"
                                onClick={() => handleOpenDocument(doc, true)}
                              >
                                Descargar
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Panel de documentos por solicitud ───────────────────────────────────────
const DocumentsPanel = ({ solicitudId, onCountChange }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [desc, setDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = React.useRef(null);

  const clearFile = () => {
    setFile(null);
    setUploadError('');
    setUploadSuccess('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const loadDocs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await laboratoryService.getDocuments(solicitudId);
      const list = res.data || [];
      setDocs(list);
      onCountChange?.(solicitudId, list.length);
    } catch {
      // silently fail on reload
    } finally {
      setLoading(false);
    }
  }, [solicitudId, onCountChange]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async () => {
    if (!file) { setUploadError('Selecciona un archivo'); return; }
    try {
      setUploading(true);
      setUploadError('');
      setUploadSuccess('');
      setProgress(0);
      await laboratoryService.uploadDocument(solicitudId, file, desc, setProgress);
      setUploadSuccess('Documento subido correctamente');
      clearFile();
      setDesc('');
      await loadDocs();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Error al subir el documento');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      setDeleting(docId);
      await laboratoryService.deleteDocument(docId);
      await loadDocs();
    } catch {
      // ignore
    } finally {
      setDeleting(null);
    }
  };

  const handleOpenDocument = async (doc, download = false) => {
    try {
      setUploadError('');
      await laboratoryService.openDocument(doc.id, doc.nombre_original, download);
    } catch {
      setUploadError('No se pudo abrir el documento');
    }
  };

  return (
    <div className="docs-panel">
      <div className="docs-upload-section">
        <h5 className="docs-section-title">Subir documento</h5>
        {uploadError && <div className="laboratories-message error">{uploadError}</div>}
        {uploadSuccess && <div className="laboratories-message success">{uploadSuccess}</div>}
        <div className="docs-upload-row">
          <label className="docs-file-label">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx"
              onChange={(e) => { setFile(e.target.files[0]); setUploadError(''); setUploadSuccess(''); }}
              disabled={uploading}
            />
            {file ? file.name : 'Elegir archivo (PDF, imagen o Word, máx. 20 MB)'}
          </label>
          {file && !uploading && (
            <button type="button" className="btn-cambiar-archivo" onClick={clearFile} title="Cambiar archivo">
              ✕ Cambiar
            </button>
          )}
          <input
            type="text"
            className="docs-desc-input"
            placeholder="Descripción (opcional)"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            disabled={uploading}
          />
          <button
            className="btn btn-primary docs-upload-btn"
            onClick={handleUpload}
            disabled={uploading || !file}
          >
            {uploading ? `${progress}%` : 'Subir'}
          </button>
        </div>
        {uploading && (
          <div className="docs-progress-bar">
            <div className="docs-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <div className="docs-list-section">
        <h5 className="docs-section-title">Documentos adjuntos</h5>
        {loading ? (
          <p className="docs-loading">Cargando...</p>
        ) : docs.length === 0 ? (
          <p className="docs-empty">Sin documentos adjuntos todavía.</p>
        ) : (
          <ul className="docs-list">
            {docs.map((doc) => (
              <li key={doc.id} className="docs-item">
                <DocIcon tipo={doc.tipo_mime} />
                <div className="docs-item-info">
                  <button
                    type="button"
                    className="docs-item-name docs-item-open"
                    onClick={() => handleOpenDocument(doc)}
                  >
                    {doc.nombre_original}
                  </button>
                  <span className="docs-item-meta">
                    {formatSize(doc.tamano)} · {formatDate(doc.created_at)} · {doc.subido_por_nombre}
                    {doc.descripcion ? ` · ${doc.descripcion}` : ''}
                  </span>
                </div>
                <button
                  className="btn-doc-download"
                  onClick={() => handleOpenDocument(doc, true)}
                  title="Descargar documento"
                >
                  Descargar
                </button>
                <button
                  className="btn-icon-danger"
                  onClick={() => handleDelete(doc.id)}
                  disabled={deleting === doc.id}
                  title="Eliminar documento"
                >
                  {deleting === doc.id ? '...' : '✕'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const LaboratoriesDashboard = ({ onBack }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Formulario nueva solicitud
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [detalleSolicitud, setDetalleSolicitud] = useState('');

  // Filtros
  const [filterEstado, setFilterEstado] = useState('');

  // Fila expandida (documentos)
  const [expandedRow, setExpandedRow] = useState(null);
  const [rowDocCounts, setRowDocCounts] = useState({});

  // Actualización de estado
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Historial de paciente
  const [historialPaciente, setHistorialPaciente] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const result = await laboratoryService.getAll(filterEstado);
      setRequests(result.data || []);
    } catch {
      setError('No fue posible cargar las solicitudes de laboratorio');
    } finally {
      setLoading(false);
    }
  }, [filterEstado]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Búsqueda de paciente (nueva solicitud)
  useEffect(() => {
    const q = patientQuery.trim();
    if (q.length < 2) { setPatientResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const result = await patientService.searchByName(q, 8);
        setPatientResults(result.data || []);
      } catch { setPatientResults([]); }
    }, 250);
    return () => clearTimeout(timer);
  }, [patientQuery]);

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientQuery(patient.nombre || '');
    setPatientResults([]);
  };

  const clearSelection = () => {
    setSelectedPatient(null);
    setPatientQuery('');
    setPatientResults([]);
    setDetalleSolicitud('');
  };

  const handleCreateRequest = async () => {
    if (!selectedPatient?.id) { setError('Selecciona un paciente para la solicitud'); return; }
    if (!detalleSolicitud.trim()) { setError('Describe qué estudios se solicitarán'); return; }
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await laboratoryService.create({ paciente_id: selectedPatient.id, detalle_solicitud: detalleSolicitud.trim() });
      setSuccess('Solicitud de laboratorio creada exitosamente');
      clearSelection();
      await loadRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'No fue posible crear la solicitud');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (solicitudId, newEstado) => {
    setUpdatingStatus(solicitudId);
    try {
      await laboratoryService.updateStatus(solicitudId, newEstado);
      setRequests((prev) =>
        prev.map((r) => r.id === solicitudId ? { ...r, estado: newEstado } : r)
      );
    } catch {
      setError('No se pudo actualizar el estado');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const handleDocCountChange = useCallback((solicitudId, count) => {
    setRowDocCounts((prev) => ({ ...prev, [solicitudId]: count }));
  }, []);

  const filteredRequests = requests;

  return (
    <div className="laboratories-container">
      <div className="laboratories-header">
        <button onClick={onBack} className="btn btn-secondary">← Volver al Dashboard</button>
        <h2>Dashboard de Laboratorios</h2>
      </div>

      {/* ── Nueva Solicitud ── */}
      <div className="laboratories-card">
        <h3>Nueva Solicitud de Laboratorio</h3>

        {error && <div className="laboratories-message error">{error}</div>}
        {success && <div className="laboratories-message success">{success}</div>}

        <div className="form-group">
          <label>Paciente asociado</label>
          <input
            type="text"
            value={patientQuery}
            onChange={(e) => {
              setPatientQuery(e.target.value);
              if (selectedPatient && e.target.value !== selectedPatient.nombre) setSelectedPatient(null);
            }}
            placeholder="Buscar paciente por nombre..."
          />
          {patientResults.length > 0 && (
            <div className="patient-results-list">
              {patientResults.map((p) => (
                <button key={p.id} type="button" className="patient-result-item" onClick={() => handleSelectPatient(p)}>
                  <strong>{p.nombre}</strong>
                  <small>{p.genero} · consultas: {p.total_consultas || 0}</small>
                </button>
              ))}
            </div>
          )}
          {selectedPatient && (
            <small className="selected-patient-info">Paciente seleccionado: {selectedPatient.nombre}</small>
          )}
        </div>

        <div className="form-group">
          <label>¿Qué estudios se van a realizar?</label>
          <textarea
            value={detalleSolicitud}
            onChange={(e) => setDetalleSolicitud(e.target.value)}
            rows="4"
            placeholder="Ejemplo: Biometría hemática completa, química sanguínea de 6 elementos, examen general de orina..."
          />
        </div>

        <div className="laboratories-actions">
          <button className="btn btn-secondary" onClick={clearSelection} disabled={saving}>Limpiar</button>
          <button className="btn btn-primary" onClick={handleCreateRequest} disabled={saving}>
            {saving ? 'Guardando...' : 'Solicitar Laboratorio'}
          </button>
        </div>
      </div>

      {/* ── Solicitudes registradas ── */}
      <div className="laboratories-card">
        <div className="requests-card-header">
          <h3>Solicitudes registradas</h3>
          <div className="requests-filters">
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos los estados</option>
              {ESTADOS.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
            <button className="btn btn-secondary btn-sm" onClick={loadRequests} disabled={loading}>
              {loading ? '...' : '↻ Actualizar'}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="loading-text">Cargando solicitudes...</p>
        ) : filteredRequests.length === 0 ? (
          <p className="empty-text">No hay solicitudes registradas{filterEstado ? ` con estado "${getEstadoLabel(filterEstado)}"` : ''}.</p>
        ) : (
          <div className="laboratories-table-wrapper">
            <table className="laboratories-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Paciente</th>
                  <th>Detalle</th>
                  <th>Estado</th>
                  <th>Origen</th>
                  <th>Solicitó</th>
                  <th>Documentos</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((item) => {
                  const isExpanded = expandedRow === item.id;
                  const isUpdating = updatingStatus === item.id;
                  const docCount = rowDocCounts[item.id] ?? item.total_documentos ?? 0;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={isExpanded ? 'row-expanded' : ''}>
                        <td className="td-fecha">{formatDate(item.created_at)}</td>
                        <td>
                          <button
                            className="patient-link"
                            onClick={() => setHistorialPaciente({ id: item.paciente_id, nombre: item.paciente_nombre })}
                            title="Ver historial de laboratorios"
                          >
                            {item.paciente_nombre}
                          </button>
                        </td>
                        <td className="td-detalle">
                          <span title={item.detalle_solicitud}>
                            {item.detalle_solicitud.length > 80
                              ? item.detalle_solicitud.slice(0, 80) + '…'
                              : item.detalle_solicitud}
                          </span>
                        </td>
                        <td>
                          <div className={`status-select-wrapper ${getEstadoClass(item.estado)}`}>
                            <select
                              value={item.estado}
                              onChange={(e) => handleStatusChange(item.id, e.target.value)}
                              disabled={isUpdating}
                              className="status-select"
                              title="Cambiar estado"
                            >
                              {ESTADOS.map((e) => (
                                <option key={e.value} value={e.value}>{e.label}</option>
                              ))}
                            </select>
                            {isUpdating && <span className="status-spinner" />}
                          </div>
                        </td>
                        <td>{item.consulta_id ? `Consulta #${item.consulta_id}` : 'Dashboard'}</td>
                        <td>{item.solicitado_por_nombre}</td>
                        <td>
                          <button
                            className={`docs-toggle-btn ${isExpanded ? 'active' : ''}`}
                            onClick={() => toggleExpand(item.id)}
                            title={isExpanded ? 'Cerrar documentos' : 'Ver / subir documentos'}
                          >
                            <span className="docs-count">{docCount}</span>
                            <span className="docs-icon">{isExpanded ? '▲' : '▼'}</span>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="docs-panel-row">
                          <td colSpan={7} className="docs-panel-cell">
                            <DocumentsPanel
                              solicitudId={item.id}
                              onCountChange={handleDocCountChange}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Panel historial de paciente ── */}
      {historialPaciente && (
        <PatientHistoryPanel
          pacienteId={historialPaciente.id}
          pacienteNombre={historialPaciente.nombre}
          onClose={() => setHistorialPaciente(null)}
        />
      )}
    </div>
  );
};

export default LaboratoriesDashboard;
